import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api, mediaUrl } from '@/lib/api';
import { sanitizeHtml } from '@/lib/sanitize';
import { ChannelPageHero, ChannelEmpty } from '@/pages/WatchPage';
import { motion } from 'framer-motion';
import { ArrowLeft, Lock } from 'lucide-react';
import { COLLECT } from '@/constants/testIds';

function price(p, currency = 'usd') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(p);
}

export function CollectList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/products').then(({ data }) => { setItems(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div>
      <ChannelPageHero kicker="CHANNEL 04" title="COLLECT" blurb="Digital products, downloads, guides, and exclusive content. A small, considered vault." accent="pink" />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-20">
        {loading ? (
          <div className="pg-card p-10 text-center text-[var(--pg-ink-muted)]">Opening the vault…</div>
        ) : items.length === 0 ? (
          <ChannelEmpty title="THE VAULT IS BEING STOCKED" copy="Digital pieces will arrive here — carefully, slowly, and only when they’re worth it." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((it, i) => (
              <motion.div key={it.id} initial={{ opacity: 1, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}>
                <Link to={`/collect/${it.id}`} data-testid={COLLECT.productCard} className="pg-card pg-card-hover overflow-hidden block group" style={{ borderColor: 'rgba(255,62,165,0.20)' }}>
                  <div className="aspect-[4/5] bg-[var(--pg-navy-800)] relative overflow-hidden">
                    {(it.primary_image_url || it.cover_url) ? (
                      <img src={mediaUrl(it.primary_image_url || it.cover_url)} alt={it.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-[var(--pg-ink-faint)]"><Lock /></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[rgba(7,8,15,0.95)]" />
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      {it.featured && <span className="font-mono text-[10px] tracking-[0.32em] pg-text-glow-pink">NEW DROP</span>}
                      <h3 className="font-serif text-xl mt-2 text-[var(--pg-ink)]">{it.title}</h3>
                      {it.subtitle && <p className="text-xs text-[var(--pg-ink)] mt-1 line-clamp-2">{it.subtitle}</p>}
                    </div>
                  </div>
                  <div className="p-5 flex items-center justify-between">
                    <span className="font-mono text-sm pg-text-glow-pink">{price(it.price, it.currency)}</span>
                    <span className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">{it.file_format || ''}</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [err, setErr] = useState(null);
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [activeImageIdx, setActiveImageIdx] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      const vs = Array.isArray(data?.variants) ? data.variants : [];
      if (vs.length > 0) setSelectedVariantId(String(vs[0].id || ''));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const selectedVariant = variants.find(v => String(v.id) === selectedVariantId) || null;
  const gallery = (() => {
    // Merged gallery: explicit order → user editorial → Printful → cover_url fallback
    const printful = Array.isArray(product?.gallery_images) ? product.gallery_images : [];
    const editorial = Array.isArray(product?.user_gallery_images) ? product.user_gallery_images : [];
    const order = Array.isArray(product?.gallery_order) ? product.gallery_order : [];
    const valid = new Set([...printful, ...editorial]);
    const explicit = order.filter((u) => valid.has(u));
    const seen = new Set(explicit);
    const rest = [
      ...editorial.filter((u) => !seen.has(u)),
      ...printful.filter((u) => !seen.has(u)),
    ];
    const merged = [...explicit, ...rest];
    if (product?.cover_url && !merged.includes(product.cover_url)) merged.unshift(product.cover_url);
    return merged;
  })();
  // Ensure primary image is shown first when set
  const primaryUrl = (product?.primary_image_url || '').trim();
  if (primaryUrl && gallery.includes(primaryUrl)) {
    const idx = gallery.indexOf(primaryUrl);
    if (idx > 0) { gallery.splice(idx, 1); gallery.unshift(primaryUrl); }
  }
  const displayImage = (
    activeImageIdx > 0
      ? gallery[activeImageIdx]
      : (primaryUrl || selectedVariant?.image || gallery[0])
  ) || product?.cover_url;
  const displayPrice = selectedVariant ? Number(selectedVariant.price || 0) : Number(product?.price || 0);
  const availability = String(selectedVariant?.availability_status || '').toLowerCase();
  const unavailable = availability && availability !== 'active';

  const onBuy = () => {
    if (!product?.id) return;
    setErr(null);
    setBuying(true);
    const q = selectedVariantId ? `?variant_id=${encodeURIComponent(selectedVariantId)}` : '';
    navigate(`/checkout/${product.id}${q}`);
  };

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 text-[var(--pg-ink-muted)]">Loading…</div>;
  if (!product) return <div className="max-w-7xl mx-auto px-6 py-20"><ChannelEmpty title="NOT FOUND" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 pb-24" data-testid="collect-product-detail">
      <Link to="/collect" className="inline-flex items-center gap-2 text-xs font-mono tracking-widest text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] transition-colors">
        <ArrowLeft size={14} /> BACK TO COLLECT
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-8">
        <div className="lg:col-span-7">
          <div className="pg-card overflow-hidden" style={{ borderColor: 'rgba(255,62,165,0.22)' }} data-testid="collect-product-hero-image">
            <div className="aspect-[4/5] bg-[var(--pg-navy-800)]">
              {displayImage ? (
                <img src={mediaUrl(displayImage)} alt={product.title} className="w-full h-full object-cover" />
              ) : (
                <div className="h-full flex items-center justify-center text-[var(--pg-ink-faint)]"><Lock size={28} /></div>
              )}
            </div>
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 flex flex-wrap gap-3" data-testid="collect-product-gallery">
              {gallery.map((g, i) => (
                <button
                  key={g + i}
                  type="button"
                  data-testid={`collect-product-gallery-thumb-${i}`}
                  onClick={() => setActiveImageIdx(i)}
                  className="w-20 h-20 rounded-md overflow-hidden border transition-all"
                  style={{
                    borderColor: activeImageIdx === i ? 'rgba(255,62,165,0.7)' : 'var(--pg-border-2)',
                    opacity: activeImageIdx === i ? 1 : 0.7,
                  }}
                >
                  <img src={mediaUrl(g)} alt={`${product.title} preview ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-5">
          {product.featured && <p className="font-mono text-[10px] tracking-[0.32em] pg-text-glow-pink">NEW DROP</p>}
          <h1 className="font-serif text-3xl sm:text-4xl tracking-[-0.02em] mt-3 text-[var(--pg-ink)] leading-tight" data-testid="collect-product-title">{product.title}</h1>
          {product.subtitle && <p className="text-[var(--pg-ink)] mt-3">{product.subtitle}</p>}

          <div className="mt-6 flex items-baseline gap-3" data-testid="collect-product-price">
            <span className="font-mono text-2xl pg-text-glow-pink">{price(displayPrice, product.currency)}</span>
            {product.file_format && <span className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">{product.file_format}</span>}
          </div>

          {variants.length > 1 && (
            <div className="mt-6 space-y-3" data-testid="collect-product-variants">
              <p className="pg-label text-[10px]">CHOOSE VARIANT</p>
              <div className="flex flex-wrap gap-2">
                {variants.map(v => {
                  const isSel = String(v.id) === selectedVariantId;
                  const label = [v.color, v.size].filter(Boolean).join(' · ') || v.name || v.sku;
                  const disabled = String(v.availability_status || '').toLowerCase() === 'discontinued';
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => !disabled && setSelectedVariantId(String(v.id))}
                      data-testid={`collect-product-variant-${v.id}`}
                      disabled={disabled}
                      className="px-3 py-2 rounded-md border text-[11px] font-mono tracking-widest uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{
                        background: isSel ? 'rgba(255,62,165,0.14)' : 'transparent',
                        borderColor: isSel ? 'rgba(255,62,165,0.7)' : 'var(--pg-border-2)',
                        color: isSel ? 'var(--pg-neon-pink)' : 'var(--pg-ink-muted)',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {selectedVariant?.sku && (
                <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">SKU · {selectedVariant.sku}</p>
              )}
            </div>
          )}

          <button
            data-testid={COLLECT.buyNowButton}
            onClick={onBuy}
            disabled={buying || unavailable}
            className="mt-6 w-full pg-btn-neon justify-center !py-3.5 !text-sm disabled:opacity-60"
          >
            {unavailable ? 'UNAVAILABLE' : (buying ? 'Opening checkout…' : 'BUY — SECURE CHECKOUT')}
          </button>

          {err && <p className="mt-3 text-xs text-[var(--pg-neon-pink)] font-mono">{err}</p>}

          <div className="pg-hairline my-8" />

          <div className="pg-prose text-[var(--pg-ink)]">
            {(() => {
              const t = String(product.description || '').trim();
              const isHtml = /^<(p|h[1-6]|ul|ol|blockquote|pre|hr|div)(\s|>)/i.test(t);
              return isHtml
                ? <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(t) }} />
                : <p className="text-[var(--pg-ink)] whitespace-pre-wrap leading-relaxed">{product.description}</p>;
            })()}
          </div>

          {product.tags?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {product.tags.map(t => <span key={t} className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase font-mono px-2 py-1 rounded-full border border-[rgba(255,62,165,0.30)] text-[var(--pg-neon-pink)]">{t}</span>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
